import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { POSTS } from "@/data/posts";

const Blog = () => {
  const { t, lang } = useLang();
  const [hero, ...rest] = POSTS;

  return (
    <div data-testid="page-blog">
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pt-16 sm:pt-24 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <span className="text-[11px] uppercase tracking-[0.22em] text-[#082133] font-semibold">
              {t("blog.eyebrow")}
            </span>
            <h1 className="mt-4 font-display font-semibold text-[#111111] text-[40px] sm:text-[54px] lg:text-[64px] leading-[1.05] tracking-[-0.03em]">
              {t("blog.title")}
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[17px] leading-relaxed text-[#4B5563]">
              {t("blog.lead")}
            </p>
          </div>
        </div>
      </section>

      {/* Featured post */}
      {hero && (
        <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pb-16">
          <Link
            to={`/blog/${hero.slug}`}
            data-testid="blog-featured-post"
            className="group grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center"
          >
            <div className="lg:col-span-7 relative aspect-[4/3] rounded-[32px] overflow-hidden">
              <img
                src={hero.image}
                alt={hero.title[lang]}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
            </div>
            <div className="lg:col-span-5">
              <span className="inline-flex items-center rounded-full bg-[#DCE3DE] text-[#082133] px-3 py-1 text-[11px] uppercase tracking-[0.18em] font-semibold">
                {hero.tag[lang]}
              </span>
              <h2 className="mt-5 font-display font-semibold text-[#111111] text-[28px] sm:text-[36px] lg:text-[42px] leading-[1.1] tracking-[-0.02em]">
                {hero.title[lang]}
              </h2>
              <p className="mt-4 text-[16px] leading-relaxed text-[#4B5563]">
                {hero.excerpt[lang]}
              </p>
              <div className="mt-7 flex items-center gap-5">
                <span className="text-[13px] text-[#4B5563]">
                  {hero.date[lang]}
                </span>
                <span className="inline-flex items-center gap-2 text-[#082133] text-sm font-medium group-hover:gap-3 transition-all">
                  {t("common.readMore")}
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* Grid of rest */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pb-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {rest.map((post, i) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              data-testid={`blog-card-${i}`}
              className="group rounded-3xl bg-white border border-[#E5E7EB] overflow-hidden hover:-translate-y-1 hover:shadow-[0_25px_50px_-20px_rgba(8,33,51,0.15)] transition-all"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title[lang]}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-7">
                <div className="flex items-center gap-3 text-[12px] uppercase tracking-[0.18em]">
                  <span className="text-[#082133] font-semibold">
                    {post.tag[lang]}
                  </span>
                  <span className="text-[#E5E7EB]">·</span>
                  <span className="text-[#4B5563]">{post.date[lang]}</span>
                </div>
                <h3 className="mt-4 font-display text-xl font-semibold text-[#111111] tracking-tight leading-snug">
                  {post.title[lang]}
                </h3>
                <p className="mt-3 text-[14px] leading-relaxed text-[#4B5563]">
                  {post.excerpt[lang]}
                </p>
                <div className="mt-5 inline-flex items-center gap-2 text-[#082133] text-sm font-medium group-hover:gap-3 transition-all">
                  {t("common.readMore")}
                  <ArrowUpRight className="h-4 w-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Blog;
